import React, { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/react/24/outline';
import { useCreateFeedback } from '../hooks/api-hooks';
import { CreateFeedbackBody } from '../api/types';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}


const NEGATIVE_FEEDBACK_REASONS = [
  'Poor quality',
  'Had technical issues',
  'Other'
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [response, setResponse] = useState<boolean | null>(null); // For question 1
  const [selectedReason, setSelectedReason] = useState<string | null>(null); // For negative follow-up
  const [otherReason, setOtherReason] = useState<string>(''); // Free text for "Other"
  const [futureUse, setFutureUse] = useState<string | boolean | null>(null); // For question 3 (yes = true, no = text reason)

  const createFeedbackMutation = useCreateFeedback();

  const handleSubmit = async (): Promise<void> => {
    const feedbackData: CreateFeedbackBody[] = [];

    // Add main question response
    feedbackData.push({
      questionId: 1,
      answer: response === true ? 'true' : 'false',
    });

    // Add negative follow-up if applicable
    if (response === false) {
      feedbackData.push({
        questionId: 2,
        answer: selectedReason === 'Other' ? otherReason : (selectedReason || ''),
      });
    }

    // Add question 3 response (future use)
    if (response === true && futureUse !== null) {
      feedbackData.push({
        questionId: 3,
        answer: typeof futureUse === 'boolean' ? 'true' : futureUse,
      });
    }

    await createFeedbackMutation.mutateAsync(feedbackData);
    onClose()
    resetForm();
  };

  const isSubmitDisabled = (): boolean => {
    if (response === null) return true; // No response to the first question

    if (!response) {
      // Negative feedback requires a reason
      if (!selectedReason) return true;
      if (selectedReason === 'Other' && !otherReason.trim()) return true;
    }

    if (response) {
      // Future use requires at least "Yes" or "No"
      if (futureUse === null) return true;
      if (typeof futureUse === 'string' && !futureUse.trim()) return true; // "No" requires text
    }

    return false; // Everything is valid
  };

  const resetForm = (): void => {
    setResponse(null);
    setSelectedReason(null);
    setOtherReason('');
    setFutureUse(null);
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <div className="fixed inset-0 bg-black/25" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100">
                  <HandThumbUpIcon className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-semibold text-gray-900">
                    We Value Your Anonymous Feedback
                  </DialogTitle>
                  <p className="mt-1 text-sm text-gray-500">Did you enjoy creating your neoface?</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Feedback Options */}
            <div className="mt-6 flex justify-center gap-8">
              <button
                className={`flex items-center gap-2 rounded-full p-3 transition-colors ${
                  response === true ? 'bg-green-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                }`}
                onClick={() => setResponse(true)}
              >
                <HandThumbUpIcon className="h-6 w-6" />
              </button>
              <button
                className={`flex items-center gap-2 rounded-full p-3 transition-colors ${
                  response === false ? 'bg-red-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
                }`}
                onClick={() => setResponse(false)}
              >
                <HandThumbDownIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Negative Feedback Follow-up */}
            {response === false && (
              <div className="mt-6 space-y-4">
                <p className="text-sm font-medium text-gray-700">
                  Would you mind telling us why?
                </p>
                <div className="space-y-3">
                  {NEGATIVE_FEEDBACK_REASONS.map((reason) => (
                    <div key={reason} className="flex items-center">
                      <input
                        type="radio"
                        id={reason}
                        name="feedback-reason"
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                        checked={selectedReason === reason}
                        onChange={() => setSelectedReason(reason)}
                      />
                      <label
                        htmlFor={reason}
                        className="ml-3 text-sm text-gray-700"
                      >
                        {reason}
                      </label>
                    </div>
                  ))}
                </div>
                {selectedReason === 'Other' && (
                  <textarea
                    className="mt-2 w-full rounded-md border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Please tell us more..."
                    rows={3}
                    value={otherReason}
                    maxLength={150}
                    onChange={(e) => setOtherReason(e.target.value)}
                  />
                )}
              </div>
            )}

            {/* Future Use Follow-up */}
            {response === true && (
              <div className="mt-6 space-y-4">
                <p className="text-sm font-medium text-gray-700">
                  Do you see yourself anonymizing your content with your new Neoface?
                </p>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="yes-future"
                      name="future-usage"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                      checked={futureUse === true}
                      onChange={() => setFutureUse(true)}
                    />
                    <label
                      htmlFor="yes-future"
                      className="ml-3 text-sm text-gray-700"
                    >
                      Yes, definitely
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="no-future"
                      name="future-usage"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                      checked={typeof futureUse === 'string'}
                      onChange={() => setFutureUse('')} // Default to empty string for the "No" reason
                    />
                    <label
                      htmlFor="no-future"
                      className="ml-3 text-sm text-gray-700"
                    >
                      No, probably not
                    </label>
                  </div>
                </div>
                {typeof futureUse === 'string' && (
                  <textarea
                    className="mt-2 w-full rounded-md border border-gray-300 p-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Please tell us more..."
                    rows={3}
                    value={futureUse}
                    maxLength={150}
                    onChange={(e) => setFutureUse(e.target.value)}
                  />
                )}
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitDisabled()} // Directly pass the result of the function
                className={`rounded-md px-4 py-2 text-sm font-medium text-white transition-colors 
    ${isSubmitDisabled() ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              >
                Submit
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default FeedbackModal;
